import {type FC, memo, useCallback, useEffect, useMemo, useState} from 'react';

import classNames from 'classnames';

import type {
  PaymentPlan,
  ProgramPaymentType,
} from '@yourcoach/shared/utils/entity/program';

import {useI18n} from '@yourcoach/i18n';
import {
  createPaymentPlan,
  parsePaymentPlan,
  ProgramPaymentTypeEnum,
} from '@yourcoach/shared/utils/entity/program';

import Button from '@src/components/Button';

import styles from './PaymentPlan.module.css';

export type Props = {
  duration: number;
  onSet: (paymentPlan: PaymentPlan) => void;
  paymentPlan: PaymentPlan;
};

const CRUProgramPaymentPlan: FC<Props> = ({
  duration,
  onSet,
  paymentPlan: paymentPlanProp,
}) => {
  const {t} = useI18n();
  const [paymentType, setPaymentType] = useState(
    ProgramPaymentTypeEnum.ONE_TIME,
  );

  const parsedPaymentPlan = useMemo(
    () => parsePaymentPlan(paymentPlanProp),
    [paymentPlanProp],
  );

  useEffect(() => {
    setPaymentType(parsedPaymentPlan.paymentType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPaymentTypeChange = useCallback((type: ProgramPaymentType) => {
    setPaymentType(type);
  }, []);

  const onSetButtonClick = useCallback(() => {
    onSet(
      createPaymentPlan({
        currency: parsedPaymentPlan.parsedCurrency,
        duration,
        paymentType,
        price: parsedPaymentPlan.total,
        processing: paymentPlanProp.processing,
      }),
    );
  }, [
    duration,
    onSet,
    parsedPaymentPlan.parsedCurrency,
    parsedPaymentPlan.total,
    paymentPlanProp.processing,
    paymentType,
  ]);

  return (
    <div>
      <div className={styles.paymentTypesContainer}>
        {[ProgramPaymentTypeEnum.ONE_TIME, ProgramPaymentTypeEnum.MONTHLY].map(
          type => {
            const isSelected = type.id === paymentType.id;

            const paymentPlan = parsePaymentPlan(
              createPaymentPlan({
                currency: parsedPaymentPlan.parsedCurrency,
                duration,
                paymentType: type,
                price: parsedPaymentPlan.total,
              }),
            );

            return (
              <div
                className={classNames(
                  styles.paymentType,
                  isSelected && styles.selected,
                )}
                key={type.id}
                onClick={() => onPaymentTypeChange(type)}
              >
                <p className={styles.paymentTypeText}>{type.toString()}</p>
                <p className={styles.paymentTypePriceText}>
                  {paymentPlan.toString()}
                </p>
              </div>
            );
          },
        )}
      </div>
      <div className={styles.footer}>
        <Button disabled={!duration} onClick={onSetButtonClick}>
          {t('shared:confirm')}
        </Button>
      </div>
    </div>
  );
};

export default memo(CRUProgramPaymentPlan);
